import { useState, useMemo } from 'react';
import { Plus, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SuperAdminUser, UserStatus } from '@bliks-io/contracts';
import { useListSuperAdminUsers } from '@/pages/admin/api/queries/useSuperAdminUsers';
import {
  Button,
  Badge,
  Page,
  PageContent,
  DataTable,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { InviteSuperAdminDialog } from './InviteSuperAdminDialog';
import { ChangePlatformRoleDialog } from './ChangePlatformRoleDialog';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import {
  getUserStatusColorScheme,
  getUserStatusLabel,
  getUserStatusOptions
} from '@/lib/user-utils';

export function UserListPage() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('adminSettings.superAdmin.title'));
  const { data, isLoading, error } = useListSuperAdminUsers();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [roleUser, setRoleUser] = useState<SuperAdminUser | null>(null);

  const handleInviteUser = () => {
    setInviteDialogOpen(true);
  };

  // Define table columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<SuperAdminUser>[]>(
    () => [
      {
        accessorKey: 'email',
        header: createColumnHeader(t('adminSettings.superAdmin.columnEmail')),
        meta: { label: t('adminSettings.superAdmin.columnEmail'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span className="font-medium">{row.getValue('email')}</span>,
        enableGlobalFilter: true
      },
      {
        accessorKey: 'firstName',
        header: createColumnHeader(t('adminSettings.superAdmin.columnFirstName')),
        meta: { label: t('adminSettings.superAdmin.columnFirstName'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span>{row.getValue('firstName')}</span>,
        enableGlobalFilter: true
      },
      {
        accessorKey: 'lastName',
        header: createColumnHeader(t('adminSettings.superAdmin.columnLastName')),
        meta: { label: t('adminSettings.superAdmin.columnLastName'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span>{row.getValue('lastName')}</span>,
        enableGlobalFilter: true
      },
      {
        id: 'platformRoles',
        accessorFn: (user) => user.platformRoles?.join(' ') ?? '',
        header: createColumnHeader(t('adminSettings.superAdmin.role')),
        cell: ({ row }) => {
          // Additiv: mehrere Rollen ergeben die Vereinigung (ADR 0042). Wer
          // keine traegt, darf nichts — und das soll man sehen.
          const roles = row.original.platformRoles ?? [];
          if (roles.length === 0) {
            return (
              <span className="text-muted-foreground text-sm">
                {t('adminSettings.superAdmin.roleNone')}
              </span>
            );
          }
          return (
            <div className="flex flex-wrap gap-1">
              {roles.map((role) => (
                <Badge key={role} variant="outline">
                  {t(`adminSettings.superAdmin.roles.${role}`)}
                </Badge>
              ))}
            </div>
          );
        }
      },
      {
        accessorKey: 'status',
        header: createColumnHeader(t('adminSettings.superAdmin.columnStatus')),
        meta: {
          label: t('adminSettings.superAdmin.columnStatus'),
          filterType: 'select',
          filterOptions: getUserStatusOptions(t)
        },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <Badge colorScheme={getUserStatusColorScheme(status)}>
              {getUserStatusLabel(status, t)}
            </Badge>
          );
        },
        enableGlobalFilter: false
      },
      {
        accessorKey: 'invitedAt',
        header: createColumnHeader(t('adminSettings.superAdmin.columnInvited')),
        cell: ({ row }) => {
          const invitedAt = row.getValue('invitedAt') as number | undefined;
          if (!invitedAt) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-muted-foreground text-sm">
              {formatRelativeTime(invitedAt, i18n.language)}
            </span>
          );
        },
        enableGlobalFilter: false
      },
      {
        accessorKey: 'activatedAt',
        header: createColumnHeader(t('adminSettings.superAdmin.columnActivated')),
        cell: ({ row }) => {
          const activatedAt = row.getValue('activatedAt') as number | undefined;
          if (!activatedAt) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-muted-foreground text-sm">
              {formatRelativeTime(activatedAt, i18n.language)}
            </span>
          );
        },
        enableGlobalFilter: false
      },
      createActionsColumn<SuperAdminUser>({
        getCustomActions: (user) => [
          {
            label: t('adminSettings.superAdmin.changeRole'),
            icon: <ShieldCheck className="h-4 w-4" />,
            onClick: () => setRoleUser(user)
          }
        ]
      })
    ],
    [t, i18n.language]
  );

  return (
    <Page>
      <PageContent>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t('adminSettings.superAdmin.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('adminSettings.superAdmin.description')}
            </p>
          </div>
          <Button onClick={handleInviteUser}>
            <Plus className="w-4 h-4 mr-2" />
            {t('adminSettings.superAdmin.inviteUser')}
          </Button>
        </div>

        {/* Table */}
        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-destructive">
              {t('adminSettings.superAdmin.errorLoadingUsers')}
            </div>
          </div>
        )}

        {!error && (
          <DataTable
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            enableGlobalFilter
            enableColumnFilters
            enableUrlState
            tableId="staffUsers"
          />
        )}

        {/* Dialogs */}
        <InviteSuperAdminDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
        <ChangePlatformRoleDialog
          user={roleUser}
          onOpenChange={(open) => {
            if (!open) setRoleUser(null);
          }}
        />
      </PageContent>
    </Page>
  );
}
